import React, { useState } from "react";
import axios from "axios";
import { X, PauseCircle, TicketPercent, ExternalLink, Loader2 } from "lucide-react";
import { API, useApp } from "@/state/AppContext";
import { toast } from "sonner";

const REASONS = [
  { key: "too_expensive", label: "Zu teuer" },
  { key: "not_needed", label: "Brauche es aktuell nicht mehr" },
  { key: "missing_features", label: "Mir fehlen Funktionen" },
  { key: "technical", label: "Technische Probleme" },
  { key: "other", label: "Anderer Grund" },
];

export default function CancelFlowModal({ open, onClose }) {
  const { authHeaders, setUser, user } = useApp();
  const [step, setStep] = useState(1); // 1=survey  2=offer  3=done
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(null);

  if (!open) return null;

  const submitSurvey = async () => {
    if (!reason) { toast.error("Bitte einen Grund auswählen."); return; }
    setBusy("survey");
    try {
      await axios.post(`${API}/payments/cancel-survey`,
        { reason, note }, { headers: authHeaders });
      setStep(2);
    } catch { toast.error("Fehler beim Speichern."); }
    finally { setBusy(null); }
  };

  const acceptWinback = async () => {
    setBusy("winback");
    try {
      await axios.post(`${API}/payments/apply-winback`, {}, { headers: authHeaders });
      toast.success("Super — 30% Rabatt aktiviert!", {
        description: "Der Rabatt wird auf deine nächste Rechnung angewendet.",
      });
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rabatt konnte nicht aktiviert werden.");
    } finally { setBusy(null); }
  };

  const acceptPause = async () => {
    setBusy("pause");
    try {
      await axios.post(`${API}/payments/pause`, {}, { headers: authHeaders });
      toast.success("Abo für 30 Tage pausiert.", {
        description: "Läuft danach automatisch weiter – bis dahin nichts zu tun.",
      });
      // Locally reflect the pause on the user
      if (user) setUser({ ...user, subscription: "free" });
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Pause fehlgeschlagen.");
    } finally { setBusy(null); }
  };

  const goToStripeCancel = async () => {
    setBusy("cancel");
    try {
      const r = await axios.post(`${API}/payments/portal`, {
        return_url: window.location.origin + "/profile",
      }, { headers: authHeaders });
      window.location.href = r.data.portal_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Portal konnte nicht geöffnet werden.");
      setBusy(null);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4"
         data-testid="cancel-flow" role="dialog" aria-modal="true">
      <div className="w-full max-w-[480px] bg-white rounded-3xl shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-5 pt-4">
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
            {step === 1 ? "Kurze Frage" : "Bevor du gehst"}
          </div>
          <button onClick={onClose} data-testid="cancel-close" className="p-2 rounded-full hover:bg-slate-100">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        {step === 1 && (
          <div className="px-5 pb-6" data-testid="cancel-step-survey">
            <h2 className="mt-1 text-2xl font-bold text-slate-900">Warum möchtest du kündigen?</h2>
            <p className="mt-1 text-slate-600 text-sm">Deine Antwort hilft uns, BriefAssist besser zu machen.</p>
            <div className="mt-4 space-y-2">
              {REASONS.map(r => (
                <button
                  key={r.key} onClick={() => setReason(r.key)}
                  data-testid={`cancel-reason-${r.key}`}
                  className={`w-full text-left px-4 py-3 rounded-2xl border transition ${reason === r.key ? "bg-blue-50 border-blue-400" : "bg-white border-slate-200 hover:border-slate-300"}`}
                >
                  <span className={`text-[15px] ${reason === r.key ? "font-semibold text-blue-800" : "text-slate-800"}`}>
                    {r.label}
                  </span>
                </button>
              ))}
            </div>
            <textarea
              data-testid="cancel-note"
              value={note}
              onChange={e => setNote(e.target.value)}
              placeholder="Optional: Was hätten wir besser machen können?"
              className="mt-3 w-full min-h-[72px] rounded-2xl border border-slate-200 bg-white p-3 text-sm text-slate-800 focus:outline-none focus:border-blue-400"
            />
            <button
              onClick={submitSurvey}
              disabled={!reason || busy === "survey"}
              data-testid="cancel-continue"
              className="mt-4 w-full min-h-[52px] rounded-2xl bg-blue-600 text-white font-semibold flex items-center justify-center gap-2 disabled:bg-slate-300"
            >
              {busy === "survey" && <Loader2 className="w-5 h-5 animate-spin" />}
              Weiter
            </button>
          </div>
        )}

        {step === 2 && (
          <div className="px-5 pb-6" data-testid="cancel-step-offer">
            <h2 className="mt-1 text-2xl font-bold text-slate-900">Wir wollen dich nicht verlieren.</h2>
            <p className="mt-1 text-slate-600 text-sm">Diese zwei Angebote gelten nur jetzt:</p>

            <div className="mt-4 rounded-2xl bg-gradient-to-br from-amber-50 to-amber-100 border border-amber-200 p-4">
              <div className="flex items-center gap-2 text-amber-800 font-semibold">
                <TicketPercent className="w-5 h-5" /> 30% Rabatt · nächste Rechnung
              </div>
              <p className="mt-1 text-sm text-slate-700">Bleib dabei und zahle nur 70 % beim nächsten Einzug.</p>
              <button
                onClick={acceptWinback} disabled={!!busy}
                data-testid="cancel-accept-winback"
                className="mt-3 w-full min-h-[46px] rounded-xl bg-amber-500 text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {busy === "winback" && <Loader2 className="w-4 h-4 animate-spin" />}
                Rabatt annehmen & bleiben
              </button>
            </div>

            <div className="mt-3 rounded-2xl bg-blue-50 border border-blue-200 p-4">
              <div className="flex items-center gap-2 text-blue-800 font-semibold">
                <PauseCircle className="w-5 h-5" /> Für 30 Tage pausieren
              </div>
              <p className="mt-1 text-sm text-slate-700">Keine Abbuchung im nächsten Monat. Danach läuft dein Abo automatisch weiter.</p>
              <button
                onClick={acceptPause} disabled={!!busy}
                data-testid="cancel-accept-pause"
                className="mt-3 w-full min-h-[46px] rounded-xl bg-blue-600 text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {busy === "pause" && <Loader2 className="w-4 h-4 animate-spin" />}
                Für 30 Tage pausieren
              </button>
            </div>

            <button
              onClick={goToStripeCancel} disabled={!!busy}
              data-testid="cancel-final"
              className="mt-4 w-full min-h-[46px] rounded-xl bg-white text-slate-600 font-medium border border-slate-200 flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {busy === "cancel" ? <Loader2 className="w-4 h-4 animate-spin" /> : <ExternalLink className="w-4 h-4" />}
              Trotzdem kündigen
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
